import { Box, Typography } from '@mui/material';
import { parse, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Map } from 'immutable';
import _ from 'lodash';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@sophocles/app';
import { REV, STATUS_TYPE, getRevVersion } from '@sophocles/utils';
import { formatDateTime, formatTime } from '@sophocles/utils/formatUtils';

import {
  joinMonitor,
  joinOrNull,
  roundOrNull,
  scheduleFormatter,
  statusTypeSwitch,
  tempNa,
  toHex,
  toMappedAmps,
  toMappedWattage,
} from '../utils';

import FieldEntry from './FieldEntry';
import StatusDetailVantage from './StatusDetailVantage';

function SectionHeader({ children }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{ textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: 'text.secondary', mt: 2, mb: 0.5 }}
    >
      {children}
    </Typography>
  );
}

export default function StatusDetail({ status, unit, unitId, unitTimeZone, isCurrent, isVantage }) {
  const { t } = useTranslation();
  const { user } = useUser();

  if (!status || status.isEmpty() || status.get('iccid', undefined) === undefined) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {t('status.noStatusFound')}
        </Typography>
      </Box>
    );
  }

  if (isVantage) {
    return <StatusDetailVantage status={status} unit={unit} isCurrent={isCurrent} unitTimeZone={unitTimeZone} />;
  }

  const fw = unit?.firmware_version ?? null;
  const schedulesPerRelay = fw?.schedules_per_relay ?? 2;
  const dayOfWeekScheduling = fw?.supports_day_of_week_schedules ?? false;
  const numberOfRelays = unit?.number_of_relays ?? 4;

  const statusType = status.has('status_type') ? status.get('status_type', 0) : 0;
  const isShortStatus = statusType === STATUS_TYPE.SHORT;
  const isLongStatus = statusType === STATUS_TYPE.LONG;
  const isCRCStatus = statusType === STATUS_TYPE.CRC;
  const isCommercialStatus = statusType === STATUS_TYPE.COMMERCIAL || statusType === STATUS_TYPE.TWO_PORT_DC;
  const containsAlarmSettings = isShortStatus || isCurrent || isCommercialStatus;

  const rev = getRevVersion({ statusMap: status, unitId });

  const schedules = [
    <FieldEntry
      key="sunrise-unit-tz"
      title={t('relayController.sunrise')}
      value={
        status.get('sunrise_sent_to_unit', null) == null
          ? null
          : formatTime(parse(status.get('sunrise_sent_to_unit').split('.')[0], 'HH:mm:ss', new Date()), user)
      }
    />,
    <FieldEntry
      key="sunset-unit-tz"
      title={t('relayController.sunset')}
      value={
        status.get('sunset_sent_to_unit', null) == null
          ? null
          : formatTime(parse(status.get('sunset_sent_to_unit').split('.')[0], 'HH:mm:ss', new Date()), user)
      }
    />,
  ];

  if (isLongStatus || isCurrent || isCommercialStatus) {
    for (let i = 1; i <= numberOfRelays; i++) {
      for (let j = 1; j <= schedulesPerRelay; j++) {
        schedules.push(
          <FieldEntry
            key={i + '_' + j}
            title={t('statusView.detail.relaySchedule', 'Relay {{relay}} Schedule {{schedule}}', {
              relay: i,
              schedule: j,
            })}
            value={scheduleFormatter(status.get(`relay_${i}_schedule_${j}`), dayOfWeekScheduling, t)}
          />,
        );
      }
    }
  }

  const deviceAlarms = containsAlarmSettings
    ? Map(status.get('device_alarms_enabled', {}))
        .map((value, key) => ({ key, value }))
        .toList()
        .map(obj => (
          <FieldEntry
            key={obj.key}
            title={obj.key}
            value={obj.value ? t('general.enabled', 'Enabled') : t('general.disabled', 'Disabled')}
          />
        ))
    : [];

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 1 }}>
        {isCurrent ? t('relayController.currentStatusDetails') : t('relayController.historicalStatusDetails')}
      </Typography>

      <FieldEntry
        title={t('statusView.detail.statusType', 'Status Type')}
        value={statusTypeSwitch(status.get('status_type'), t)}
        show={status.has('status_type')}
      />
      <FieldEntry
        title={t('statusView.detail.lastStatusType', 'Last Status Type')}
        value={statusTypeSwitch(status.get('last_status_type'), t)}
        show={status.has('last_status_type')}
      />
      <FieldEntry
        title={
          isCurrent
            ? t('statusView.detail.statusId', 'Status ID')
            : t('statusView.detail.historicalStatusId', 'Historical Status ID')
        }
        value={status.get('id')}
      />

      {isCurrent && (
        <Fragment>
          <FieldEntry
            title={t('statusView.detail.lastReceiveTimeYourTz', 'Last Receive Time (Your TZ)')}
            value={formatDateTime(parseISO(status.get('last_receive_time')), user)}
            show={status.has('last_receive_time')}
          />
          <FieldEntry
            title={t('statusView.detail.lastReceiveTimeUnitTz', 'Last Receive Time (Unit TZ)')}
            value={formatDateTime(toZonedTime(parseISO(status.get('last_receive_time')), unitTimeZone), user)}
            show={status.has('last_receive_time')}
          />
        </Fragment>
      )}

      <FieldEntry
        title={t('statusView.detail.receiveTimeYourTz', 'Receive Time (Your TZ)')}
        value={
          status.get('receive_time') != null
            ? formatDateTime(parseISO(status.get('receive_time')), user)
            : t('general.unknown', 'Unknown')
        }
        show={status.has('receive_time')}
      />
      <FieldEntry
        title={t('statusView.detail.receiveTimeUnitTz', 'Receive Time (Unit TZ)')}
        value={
          status.get('receive_time') != null
            ? formatDateTime(toZonedTime(parseISO(status.get('receive_time')), unitTimeZone), user)
            : t('general.unknown', 'Unknown')
        }
        show={status.has('receive_time')}
      />

      <FieldEntry title={t('statusView.detail.iccid', 'ICCID')} value={status.get('iccid')} />
      <FieldEntry title={t('statusView.detail.ipAddress', 'IP Address')} value={status.get('ip_address')} />
      <FieldEntry
        title={t('statusView.detail.version', 'Version')}
        value={status.get('version')}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.sanityCheckRate', 'Sanity Check Rate')}
        value={`${status.get('max_data_session_minutes')} ${t('general.minutes', 'minutes')}`}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.checkInTime', 'Check In Time')}
        value={`${status.get('check_in_minutes_after_midnight')} ${t('statusView.detail.minutesAfterMidnight', 'minutes after midnight')}`}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.signalStrength', 'Signal Strength')}
        value={status.get('received_signal_strength')}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.radioTemp', 'Radio Temp (°C)')}
        value={roundOrNull(status.get('radio_temperature', null), 4)}
        show={isLongStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.cpuTemp', 'CPU Temp (°C)')}
        value={roundOrNull(status.get('cpu_temperature', null), 1)}
      />
      <FieldEntry
        title={t('statusView.detail.sensorTemp', 'Sensor Temp (°C)')}
        value={roundOrNull(status.get('temperature', null), 4)}
        show={isLongStatus || isCommercialStatus || isCurrent}
      />

      {rev === REV.A ? (
        <FieldEntry
          title={t('statusView.detail.inputVoltage', 'Input Voltage (V)')}
          value={roundOrNull(status.get('battery_voltage', null), 1)}
        />
      ) : (
        <Fragment>
          <FieldEntry
            title={t('statusView.detail.inputVoltage1', 'Input Voltage 1 (V)')}
            value={roundOrNull(status.get('input_voltage_1', null), 1)}
          />
          <FieldEntry
            title={t('statusView.detail.inputVoltage2', 'Input Voltage 2 (V)')}
            value={roundOrNull(status.get('input_voltage_2', null), 1)}
          />
        </Fragment>
      )}

      <FieldEntry
        title={t('statusView.detail.cpuInputVoltage', 'CPU Input Voltage (V)')}
        value={roundOrNull(status.get('cpu_input_voltage', null), 1)}
      />
      <FieldEntry
        title={t('statusView.detail.thermistorTemperature', 'Thermistor Temperature (°C)')}
        value={tempNa(status.get('thermistor_temperature'), t)}
      />
      <FieldEntry
        title={t('statusView.detail.upTime', 'Up Time')}
        value={status.get('uptime')}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.boardType', 'Board Type')}
        value={toHex(status.get('board_type'))}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.alternateStatus', 'Alternate Status')}
        value={toHex(status.get('alternate_status'))}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.features', 'Features')}
        value={status.get('features', null) != null ? status.get('features', []).join(', ') : null}
      />
      <FieldEntry
        title={t('statusView.detail.unitTime', 'Unit Time')}
        value={`${status.get('unit_month')}/${status.get('unit_day')}/${status.get('unit_year')}, ${status.get('current_time')}`}
        show={isLongStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.dayOfTheWeek', 'Day of the Week')}
        value={status.get('day_of_the_week_display')}
      />
      <FieldEntry
        title={t('statusView.detail.currentBias', 'Current Bias')}
        value={status.get('current_bias')}
        show={isLongStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.sampleTime', 'Sample Time')}
        value={status.get('sample_time') ? `${status.get('sample_time')} ${t('statusView.detail.mins', 'mins')}` : null}
        show={isLongStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.thresholdPercentage', 'Threshold Percentage')}
        value={status.get('threshold_percentage')}
        show={isShortStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.delayBeforeMeasuringPower', 'Delay before Measuring Power after Relay On')}
        value={status.get('on_delay') ? `${status.get('on_delay')} ${t('statusView.detail.mins', 'mins')}` : null}
        show={isShortStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.peakRelayPowerWatts', 'Peak Relay Power (watts)')}
        value={toMappedWattage(status.get('relay_max_power_watts', []), t)}
      />
      <FieldEntry
        title={t('statusView.detail.liveRelayPowerWatts', 'Live Relay Power (watts)')}
        value={toMappedWattage(status.get('relay_instantaneous_power_watts', []), t)}
      />
      <FieldEntry
        title={t('statusView.detail.peakRelayCurrentsAmps', 'Peak Relay Currents (amps)')}
        value={toMappedAmps(status.get('relay_max_currents_amps', []), t)}
      />
      <FieldEntry
        title={t('statusView.detail.liveRelayCurrentsAmps', 'Live Relay Currents (amps)')}
        value={toMappedAmps(status.get('relay_instantaneous_currents_amps', []), t)}
      />
      <FieldEntry
        title={t('statusView.detail.peakRelayCurrentsNoUnits', 'Peak Relay Currents (no units)')}
        value={joinOrNull(status.get('relay_max_currents_arbitrary_units', []), t)}
      />
      <FieldEntry
        title={t('statusView.detail.liveRelayCurrentsNoUnits', 'Live Relay Currents (no units)')}
        value={joinOrNull(status.get('relay_instantaneous_currents_arbitrary_units', []), t)}
      />
      <FieldEntry
        title={t('statusView.detail.monitoringEnabledRelays', 'Monitoring Enabled Relays')}
        value={joinMonitor(status.get('relay_monitoring_enabled'), t)}
      />
      <FieldEntry
        title={t('statusView.detail.activeRelays', 'Active Relays')}
        value={joinMonitor(status.get('relay_power_states'), t)}
      />
      <FieldEntry
        title={t('statusView.detail.relayThresholds', 'Relay Thresholds')}
        value={status.get('relay_thresholds', []) !== null ? status.get('relay_thresholds', []).join(', ') : null}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.tamperVoltageRaw', 'Tamper Voltage (raw)')}
        value={status.get('tamper_voltage')}
        show={isLongStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.scheduleLockout', 'Schedule Lockout')}
        value={status.get('schedule_lockout')}
        show={isShortStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.powerLossTimeout', 'Power Loss Timeout')}
        value={status.get('power_loss_timeout')}
        show={isShortStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.powerRestoreTimeout', 'Power Restore Timeout')}
        value={status.get('power_restore_timeout')}
        show={isShortStatus || isCommercialStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.checksum', 'Checksum')}
        value={toHex(status.get('checksum'))}
        show={isShortStatus || isCRCStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.checksum1', 'Checksum 1')}
        value={toHex(status.get('checksum_1'))}
        show={isShortStatus || isCRCStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.comChecksum', 'Com Checksum')}
        value={toHex(status.get('com_checksum'))}
        show={isShortStatus || isCRCStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.relayChecksum', 'Relay Checksum')}
        value={toHex(status.get('relay_checksum'))}
        show={isShortStatus || isCRCStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.sensorsChecksum', 'Sensors Checksum')}
        value={toHex(status.get('sensors_checksum'))}
        show={isShortStatus || isCRCStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.switchesChecksum', 'Switches Checksum')}
        value={toHex(status.get('switches_checksum'))}
        show={isShortStatus || isCRCStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.upgradeChecksum', 'Upgrade Checksum')}
        value={toHex(status.get('upgrade_checksum'))}
        show={isShortStatus || isCRCStatus || isCurrent}
      />
      <FieldEntry
        title={t('statusView.detail.realTimeClockChecksum', 'Real Time Clock Checksum')}
        value={toHex(status.get('real_time_clock_checksum'))}
        show={isShortStatus || isCRCStatus || isCurrent}
      />

      {status.get('generated_energy', null) !== null && (
        <SectionHeader>{t('statusView.detail.energy', 'Energy')}</SectionHeader>
      )}
      <FieldEntry
        title={t('statusView.detail.generatedEnergyWh', 'Generated Energy (Wh)')}
        value={roundOrNull(status.get('generated_energy', null), 4)}
      />
      <FieldEntry
        title={t('statusView.detail.positiveConsumedEnergyWh', 'Positive Consumed Energy (Wh)')}
        value={roundOrNull(status.get('positive_consumed_energy', null), 4)}
      />
      <FieldEntry
        title={t('statusView.detail.negativeConsumedEnergyWh', 'Negative Consumed Energy (Wh)')}
        value={roundOrNull(status.get('negative_consumed_energy', null), 4)}
      />

      {status.get('delay_before_relays_power_off_seconds', null) != null && (
        <SectionHeader>
          {t('statusView.detail.intrusionSensorConfiguration', 'Intrusion Sensor Configuration')}
        </SectionHeader>
      )}
      <FieldEntry
        title={t('statusView.detail.delayBeforeRelaysPowerOff', 'Delay Before Relays Power Off')}
        value={
          status.get('delay_before_relays_power_off_seconds', null) != null
            ? `${status.get('delay_before_relays_power_off_seconds')} ${t('statusView.detail.secondsParen', '(seconds)')}`
            : null
        }
      />
      <FieldEntry
        title={t('statusView.detail.automaticAlarmCancelTime', 'Automatic Alarm Cancel Time')}
        value={
          status.get('automatic_alarm_cancel_time_minutes', null) != null
            ? `${status.get('automatic_alarm_cancel_time_minutes')} ${t('statusView.detail.minsParen', '(mins)')}`
            : null
        }
      />
      <FieldEntry
        title={t('statusView.detail.delayBeforeRearm', 'Delay Before Rearm')}
        value={
          status.get('automatic_rearm_time_minutes', null) != null
            ? `${status.get('automatic_rearm_time_minutes')} ${t('statusView.detail.minsParen', '(mins)')}`
            : null
        }
      />
      <FieldEntry
        title={t('statusView.detail.relaysToPowerOff', 'Relays to Power Off')}
        value={status.get('relays_to_power_off', null)}
      />

      {schedules.length > 0 && (
        <SectionHeader>{t('statusView.detail.relaySchedules', 'Relay Schedules')}</SectionHeader>
      )}
      {schedules}

      {containsAlarmSettings && <SectionHeader>{t('statusView.detail.deviceAlarms', 'Device Alarms')}</SectionHeader>}
      {deviceAlarms}

      {(rev === REV.A || rev === REV.B) && isCurrent && (
        <Fragment>
          <SectionHeader>{t('statusView.detail.thresholdActions', 'Threshold / Actions')}</SectionHeader>
          <FieldEntry
            title={t('statusView.detail.lowVoltageThresholdsV1', 'Low Voltage Thresholds v1')}
            value={roundOrNull(status.get('low_voltage_thresholds_v1', null), 1)}
          />
          <FieldEntry
            title={t('statusView.detail.highVoltageThresholdsV1', 'High Voltage Thresholds v1')}
            value={roundOrNull(status.get('high_voltage_thresholds_v1', null), 1)}
          />
          <FieldEntry
            title={t('statusView.detail.lowVoltageThresholdsV2', 'Low Voltage Thresholds v2')}
            value={roundOrNull(status.get('low_voltage_thresholds_v2', null), 1)}
          />
          <FieldEntry
            title={t('statusView.detail.highVoltageThresholdsV2', 'High Voltage Thresholds v2')}
            value={roundOrNull(status.get('high_voltage_thresholds_v2', null), 1)}
          />
          <FieldEntry
            title={t('statusView.detail.relay1ActionV1Low', 'Relay 1 Action v1 Low')}
            value={status.get('relay_1_action_v1_low', null)}
          />
          <FieldEntry
            title={t('statusView.detail.relay1ActionV1High', 'Relay 1 Action v1 High')}
            value={status.get('relay_1_action_v1_high', null)}
          />
          <FieldEntry
            title={t('statusView.detail.relay2ActionV1Low', 'Relay 2 Action v1 Low')}
            value={status.get('relay_2_action_v1_low', null)}
          />
          <FieldEntry
            title={t('statusView.detail.relay2ActionV1High', 'Relay 2 Action v1 High')}
            value={status.get('relay_2_action_v1_high', null)}
          />
          <FieldEntry
            title={t('statusView.detail.relay1ActionV2Low', 'Relay 1 Action v2 Low')}
            value={status.get('relay_1_action_v2_low', null)}
          />
          <FieldEntry
            title={t('statusView.detail.relay1ActionV2High', 'Relay 1 Action v2 High')}
            value={status.get('relay_1_action_v2_high', null)}
          />
          <FieldEntry
            title={t('statusView.detail.relay2ActionV2Low', 'Relay 2 Action v2 Low')}
            value={status.get('relay_2_action_v2_low', null)}
          />
          <FieldEntry
            title={t('statusView.detail.relay2ActionV2High', 'Relay 2 Action v2 High')}
            value={status.get('relay_2_action_v2_high', null)}
          />
          <FieldEntry
            title={t('statusView.detail.relayFunctions', 'Relay Functions')}
            value={status.get('relay_function', []) !== null ? status.get('relay_function', []).join(', ') : null}
            show={isShortStatus || isCommercialStatus || isCurrent}
          />
          <FieldEntry
            title={t('statusView.detail.relayPulseTimesSeconds', 'Relay Pulse Times (seconds)')}
            value={status.get('relay_pulse_time', []) !== null ? status.get('relay_pulse_time', []).join(', ') : null}
            show={isShortStatus || isCommercialStatus || isCurrent}
          />
        </Fragment>
      )}
    </Box>
  );
}
